import React, { useEffect } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import {
  selectCurrentOrganizationId,
  selectOrganizationsData,
  selectOrganizationUsersData,
} from '../../store/organization/organization-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { useAppDispatch, useAppSelector } from '../../store';
import CustomTable from '../misc/CustomTable';
import { resetUsersRestState } from '../../store/organization/organization-slice';
import { useSearchParams } from 'react-router-dom';
import {
  getOrganizationUsersById,
  patchOrganizationById,
} from '../../store/organization/organization-actions';
import { FilterKeys } from '@common/enums/query-params/filters';
import { ContactPersonsIcon, DeleteIcon } from '@common/utils/icons/icons';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { selectIsUserAdminOrHigher } from '../../store/general/general-slice-selectors';

const UsersTable: React.FC<{ onUserSelect: (id: string, name: string) => void }> = ({
  onUserSelect,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [filterParams, setFilterParams] = useSearchParams();
  const querySearch = filterParams.get(FilterKeys.Search);

  const usersData = useAppSelector((state) => selectOrganizationUsersData(state));
  const currentOrganizationId = useAppSelector((state) => selectCurrentOrganizationId(state));
  const organizationData = useAppSelector((state) => selectOrganizationsData(state));
  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));

  useEffect(() => {
    dispatch(resetUsersRestState({ getStatus: HttpFetchStatus.Idle }));
  }, [dispatch]);

  useEffect(() => {
    if (usersData.rest.getStatus === HttpFetchStatus.Idle && currentOrganizationId != null) {
      dispatch(
        getOrganizationUsersById({
          org: currentOrganizationId,
          s: querySearch ?? undefined,
        }),
      );
    }
  }, [currentOrganizationId, dispatch, querySearch, usersData.rest.getStatus]);

  const rowClickHandler = (id: string) => {
    setFilterParams((prevState) => {
      prevState.set(FilterKeys.UserId, id);
      return prevState;
    });
  };

  const renderRole = (params: GridRenderCellParams) => {
    return t('user-role-' + params.value);
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: t('table-header-name') as string,
      width: 200,
      renderCell: (params) => <NameCell id={params.id as string} />,
    },
    {
      field: 'role',
      headerName: t('table-header-user-role') as string,
      renderCell: renderRole,
      width: 100,
    },
    { field: 'email', headerName: t('table-header-email') as string, width: 300 },
    { field: 'phoneNumber', headerName: t('table-header-phone-number') as string, width: 200 },
    {
      field: 'location',
      headerName: t('table-header-location') as string,
      width: 200,
      valueGetter: (_, row) => {
        return row.organization.name;
      },
    },
    {
      field: 'open',
      headerName: '',
      disableReorder: true,
      align: 'center',
      renderCell: (params) => (
        <Stack direction="row" width="100%" justifyContent="end" height="100%">
          <Button
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              rowClickHandler?.(params.row.id);
            }}
          >
            {t('button-open')}
          </Button>
        </Stack>
      ),
    },
  ];
  if (isAdmin) {
    columns.push({
      field: 'actions',
      headerName: '',
      disableReorder: true,
      renderCell: (params) => (
        <AddDeleteCellButton id={params.id as string} onUserSelect={onUserSelect} />
      ),
    });
  }

  const sortedUsers = [...(usersData.data ?? [])].sort((a, b) => {
    if (organizationData.current?.contactIds.some((cId) => cId === a.id)) return -1;
    if (
      !organizationData.current?.contactIds.some((cId) => cId === a.id) &&
      organizationData.current?.contactIds.some((cId) => cId === b.id)
    )
      return 1;
    return 0;
  });

  return (
    <React.Fragment>
      <CustomTable
        hideFooter
        getStatus={usersData.rest.getStatus}
        loadingMessage={t('loading-users')}
        dataLoaded={usersData.data != null}
        failedToLoadMessage={usersData.rest.getError?.message ?? t('error-loading-users')}
        data={sortedUsers}
        emptyDataMessage={t('info-no-users')}
        columns={columns}
      />
    </React.Fragment>
  );
};

export default UsersTable;

const NameCell: React.FC<{ id: string }> = ({ id }) => {
  const organizationData = useAppSelector((state) => selectOrganizationsData(state));
  const usersData = useAppSelector((state) => selectOrganizationUsersData(state));

  const user = usersData.data?.find((u) => u.id === id);

  return organizationData.current?.contactIds.some((cId) => cId === id) ? (
    <Stack direction="row" spacing={1} alignItems="center">
      <ContactPersonsIcon color="action" />
      <Typography variant="inherit">{user?.name}</Typography>
    </Stack>
  ) : (
    <Typography variant="inherit">{user?.name}</Typography>
  );
};

const AddDeleteCellButton: React.FC<{
  id: string;
  onUserSelect: (id: string, name: string) => void;
}> = ({ id, onUserSelect }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const organizationData = useAppSelector((state) => selectOrganizationsData(state));
  const organizationUsers = useAppSelector((state) => selectOrganizationUsersData(state));

  const user = organizationUsers.data?.find((u) => u.id === id);
  const isDeleteButton = organizationData.current?.contactIds.some((cId) => cId === id) ?? false;

  const clickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (organizationData.current?.contactIds != null) {
      if (!isDeleteButton) {
        onUserSelect(id, user?.name ?? '');
      }
      dispatch(
        patchOrganizationById({
          id: organizationData.current.id,
          data: {
            contactIds: isDeleteButton
              ? organizationData.current.contactIds.filter((cId) => cId !== id)
              : [...organizationData.current.contactIds, id],
          },
        }),
      );
    }
  };

  return (
    <Stack direction="row" width="100%" justifyContent="end" height="100%">
      <Button
        startIcon={isDeleteButton ? <DeleteIcon /> : <ContactPersonsIcon />}
        onClick={clickHandler}
        fullWidth
        color={isDeleteButton ? 'error' : 'primary'}
      >
        {isDeleteButton
          ? user?.organization.id === organizationData.current?.id
            ? t('button-unassign-as-contact-person')
            : t('button-remove-contact-person')
          : t('button-assign-as-contact-person')}
      </Button>
    </Stack>
  );
};
